import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  Button,
  Paper,
  CircularProgress,
  Typography,
} from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import LightTooltip from '../../../components/LightTooltip';
import LayoutTooltip from '../../../components/LayoutTooltip';
import InfoIcon from '../../../components/InfoIcon';
import HighlightedInput from '../../../components/HighlightedInput';
import NowrapWrapper from '../../../components/NowrapWrapper';
import FunctionalTable from '../../../components/FunctionalTable';
import OverLayLoader from '../../../components/OverLayLoader';
import FileProcessingCard from '../../../components/FileProcessingCard';

import {
  classify,
  uploadFile,
  classifyFile,
  getLanguages,
  getFileResult,
} from '../../../services';
import {
  languageOption,
  STATUS_KEYS,
  SOURCE,
  processingFileResultStatus,
} from '../../../helpers/options';
import { CLASSIFICATION_KEYS, CONSTANT_KEYS } from '../../../helpers/tableKeys';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import { numberInputOnWheelPreventChange } from '../../../utils/numberInputOnWheelPreventChange';
import useTitle from '../../../hooks/useTitle';
import useSessionStorage from '../../../hooks/useSessionStorage';
import statusCodes from '../../../utils/statusCodes';
import isRussian from '../../../utils/isRussian';
import { useAuth } from '../../../context/auth/AuthContext';

const {
  NAMES,
  CODING,
  SCORE,
  NUMBER,
  CLEANED_SERVICE_NAME,
  TASK_ID,
  COLUMN,
  NONDICT,
  KEY,
} = CLASSIFICATION_KEYS;
const { SERVICE_NAMES, LANGUAGE_NAME, PROFILE_NAME, CLEAN, SERVICE_NAME } =
  CONSTANT_KEYS;

const ClassificationView = () => {
  const [data, setData] = useState({
    language: '',
    profile: '',
    score: '0.6',
    text: '',
    source: SOURCE.TEXT,
  });
  const [file, setFile] = useState(null);
  const [fileResultUrl, setFileResultUrl] = useState('/1323');
  const [fileResultTaskId, setFileResultTaskId] = useState('13');
  const [fileName, setFileName] = useState('');
  const [isFileLoading, setIsFileLoading] = useState(false);

  const [columnsData, setColumnsData] = useState(null);
  const [column, setColumn] = useState('');
  const [cleanedText, setCleanedText] = useState('');
  const [nondict, setNondict] = useState('');
  const [classification, setClassification] = useState([]);
  const [status, setStatus] = useState(STATUS_KEYS.IDLE);
  const [profiles, setProfiles] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);

  const fileInputRef = useRef(null);
  const intervalRef = useRef(null);

  const { user } = useAuth();

  const { t } = useTranslation(['classification', 'common', 'promptbar']);

  const title = t(`classification:classification`);
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  const [sessionData, setSessionData] = useSessionStorage(
    'classificationColumns',
    {
      profiles: '',
    },
  );

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchLanguages = async () => {
      try {
        const langs = await getLanguages();
        setLanguages(langs.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsOptionsLoading(false);
      }
    };

    setProfiles(user.profiles);
    fetchLanguages();
  }, [user]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Вычисляем язык
  const resolvedLanguage = useMemo(() => {
    if (!languages.length || !user) return '';

    return (
      languages.find(el => el.id === user.data_language_id)?.name ||
      languageOption[0]
    );
  }, [languages, user]);

  // Вычисляем итоговый профиль
  const resolvedProfile = useMemo(() => {
    if (!user?.profiles?.length) return '';

    // Находим default профиль пользователя
    const firstDefaultProfile = user.profiles.find(
      pr => pr.is_default,
    )?.profile_name;

    const defaultProfile =
      firstDefaultProfile || user.profiles[0]?.profile_name;

    // Проверяем валидность сохранённого профиля
    const isSessionProfileValid = user.profiles.some(
      p => p.profile_name === sessionData.profiles,
    );
    // Приоритет:
    // sessionStorage → если валиден
    // иначе → defaultProfile
    return isSessionProfileValid ? sessionData.profiles : defaultProfile;
  }, [user, sessionData.profiles]);

  useEffect(() => {
    if (!resolvedProfile || !resolvedLanguage) return;

    setData(prev => ({
      ...prev,
      profile: resolvedProfile,
      language: resolvedLanguage,
    }));
  }, [resolvedProfile, resolvedLanguage]);

  const handleSubmit = async () => {
    setStatus(STATUS_KEYS.PENDING);
    setFileResultTaskId('');
    setFileResultUrl('');
    setFileName('');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try {
      if (data.source === SOURCE.TEXT) {
        const newData = {
          [SERVICE_NAMES]: data.text ? [data.text] : [],
          [LANGUAGE_NAME]: data.language,
          [PROFILE_NAME]: data.profile,
          [CLEAN]: true,
        };
        const params = { [SCORE]: data.score };
        const result = await classify(newData, params);
        if (result) {
          const filteredCodeNames = result.data.flatMap(item =>
            item.codes.map(code => ({
              ...code,
              names: code.names.filter(
                nameObj => nameObj.language.toLowerCase() === data.language,
              ),
            })),
          );

          setClassification(filteredCodeNames);
          setNondict(result.data.map(el => el[NONDICT]).join(';'));
          setCleanedText(
            result.data.map(el => el[CLEANED_SERVICE_NAME]).join(';'),
          );
          setStatus(STATUS_KEYS.RESOLVED);
        }
      }

      if (data.source === SOURCE.FILE) {
        if (!columnsData && !columnsData?.[TASK_ID]) {
          return;
        }
        const newData = {
          [LANGUAGE_NAME]: data.language,
          [PROFILE_NAME]: data.profile,
          [SCORE]: data.score,
          [TASK_ID]: columnsData[TASK_ID],
          [COLUMN]: column,
        };
        const result = await classifyFile(newData);
        if (result) {
          const taskId = result.data.task_id;
          setFileResultTaskId(taskId);

          intervalRef.current = setInterval(async () => {
            try {
              const response = await getFileResult(taskId);

              if (
                (response?.data?.status ===
                  processingFileResultStatus.SUCCESS &&
                  response?.data?.file_url) ||
                response?.data?.status === processingFileResultStatus.FAILURE
              ) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                setFileName(response?.data?.orig_filename || '');
                setFileResultUrl(response?.data?.file_url);
                setStatus(
                  response.data.status === processingFileResultStatus.FAILURE
                    ? STATUS_KEYS.REJECTED
                    : STATUS_KEYS.RESOLVED,
                );
              }
            } catch (error) {
              console.error('Ошибка при получении результата', error);
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              createErrorNotification();
              setStatus(STATUS_KEYS.REJECTED);
            }
          }, 2000);
        }
      }
    } catch (error) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      createErrorNotification();
      setStatus(STATUS_KEYS.REJECTED);
      console.log(error);
    }
  };

  const handleInputChange = event => {
    const { value, name } = event.target;

    switch (name) {
      case 'source':
        if (value === 'text') {
          setData(prev => ({ ...prev, [name]: value }));
          setFile(null);
          setFileResultUrl('');
          setFileResultTaskId('');
          setFileName('');
          clearInterval(intervalRef.current);
          setColumnsData(null);
          setColumn('');
          setStatus(STATUS_KEYS.IDLE);
        }
        if (value === 'file') {
          setData(prev => ({ ...prev, [name]: value, text: '' }));
          setStatus(STATUS_KEYS.IDLE);
        }
        break;

      case 'column':
        setColumn(value);
        break;

      default:
        setData(prev => ({ ...prev, [name]: value }));

        // Если изменился профиль — сохраняем его в sessionStorage
        if (name === 'profile') {
          setSessionData(prev => ({
            ...prev,
            profiles: value,
          }));
        }
        break;
    }
  };

  const handleFileChange = async newFile => {
    try {
      setFile(null);
      setColumnsData(null);
      setFileResultUrl('');
      setFileResultTaskId('');
      setFileName('');
      setStatus(STATUS_KEYS.IDLE);
      clearInterval(intervalRef.current);
      setColumn('');

      if (!newFile) {
        return;
      }

      setTimeout(() => {
        setFile(newFile);
        setFileName(newFile.name);
      }, 0);

      setIsFileLoading(true);

      const response = await uploadFile({ file: newFile });

      if (response) {
        setColumnsData(response.data);
        const serviceName = response.data.columns.find(col => {
          return col?.toString()?.toLowerCase() === SERVICE_NAME;
        });
        setColumn(serviceName || response.data.columns[0]);
      }
      setIsFileLoading(false);
    } catch (error) {
      setFile(null);
      setIsFileLoading(false);
      if (
        error?.response?.status === statusCodes.HTTP_422_UNPROCESSABLE_ENTITY
      ) {
        const message = error?.response?.data?.detail || '';
        const isUserMessage = isRussian(message);
        if (isUserMessage) {
          return createErrorNotification(message);
        } else {
          const isFormatError = /[no key such]/i.test(message);
          if (isFormatError) {
            return createErrorNotification(
              t(
                'File reading error. Please ensure the uploaded file is in xlsx format',
              ),
            );
          }
          return createErrorNotification();
        }
      }
      createErrorNotification();
    } finally {
      fileInputRef.current.value = null;
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t('key')} />,
        accessor: KEY,
        className: 'center',
        style: { width: '30%' },
      },
      {
        Header: <NowrapWrapper title={t('code')} />,
        accessor: NUMBER,
        className: 'center',
        style: { width: '10%' },
      },
      {
        Header: <NowrapWrapper title={t('score')} />,
        accessor: SCORE,
        className: 'center',
        style: { width: '10%' },
        Cell: ({ value }) => {
          if (value === null || value === undefined) {
            return '';
          } else {
            return Math.floor(value * 100) / 100;
          }
        },
      },
      {
        Header: <NowrapWrapper title={t('coding')} />,
        accessor: CODING,
        className: 'center',
        style: { width: '10%' },
      },
      {
        Header: <NowrapWrapper title={t('name')} />,
        accessor: NAMES,
        className: 'center',
        style: { width: '40%' },
        Cell: ({ value }) => value.map(el => el.name).join('; '),
      },
    ],
    [t],
  );

  const isClassifyBtnDisabled =
    (data.source === SOURCE.TEXT && !data.text) ||
    (data.source === SOURCE.FILE &&
      (!columnsData || !columnsData?.[TASK_ID])) ||
    status === STATUS_KEYS.PENDING ||
    isFileLoading;

  return (
    <Paper elevation={3} sx={{ padding: '2.5rem 2rem 2rem' }}>
      {(isFileLoading || isOptionsLoading) && <OverLayLoader />}
      <Box
        component="form"
        autoComplete="off"
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '2.5rem',
          }}
        >
          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-language-label" shrink={false}>
              {t('common:dictionaryLanguage')}
            </InputLabel>
            <Select
              labelId="Select-language-label"
              id="Select-language"
              name="language"
              value={data.language}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('common:dictionaryLanguage')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              {languages.map(lng => (
                <MenuItem key={lng.id} value={lng.name}>
                  <ListItemText primary={lng.name.toUpperCase()} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-profile-label" shrink={false}>
              {t('common:profile')}
            </InputLabel>
            <Select
              labelId="Select-profile-label"
              id="Select-profile"
              name="profile"
              value={data.profile}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('common:profile')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              {profiles.map(el => (
                <MenuItem key={el.id} value={el.profile_name}>
                  <ListItemText primary={el.profile_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Input-score-label" shrink={false}>
              {t('Threshold score')}
            </InputLabel>
            <OutlinedInput
              id="Input-score"
              name="score"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.01 }}
              value={data.score}
              onChange={handleInputChange}
              onWheel={numberInputOnWheelPreventChange}
            />
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-file-label" shrink={false}>
              {t('common:source')}
            </InputLabel>
            <Select
              labelId="Select-source-label"
              id="Select-source"
              name="source"
              value={data.source}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('common:source')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              <MenuItem key={SOURCE.TEXT} value={SOURCE.TEXT}>
                <ListItemText primary={t('common:text')} />
              </MenuItem>
              <MenuItem key={SOURCE.FILE} value={SOURCE.FILE}>
                <ListItemText primary={t('common:file')} />
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '2.5rem',
            mt: '3rem',
          }}
        >
          {data.source === SOURCE.TEXT && (
            <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
              <InputLabel id="Input-score-label" shrink={false}>
                {t('common:text')}
                <LightTooltip
                  title={<LayoutTooltip lang={data.language} />}
                  arrow
                  placement="right"
                >
                  <InfoIcon />
                </LightTooltip>
              </InputLabel>

              <HighlightedInput
                inputProps={{ id: 'Input-text', name: 'text' }}
                value={data.text}
                onChange={handleInputChange}
                lang={data.language}
                spaceMarkerMode="none"
              />
            </FormControl>
          )}

          {data.source === SOURCE.FILE && (
            <>
              <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                <InputLabel id="Input-file-label" shrink={false}>
                  {t('common:file')}
                </InputLabel>
                <MuiFileInput
                  inputRef={fileInputRef}
                  value={file}
                  onChange={handleFileChange}
                  InputProps={{
                    inputProps: {
                      accept: '.xlsx',
                    },
                    startAdornment: <AttachFileIcon />,
                  }}
                />
              </FormControl>
              {columnsData && (
                <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                  <InputLabel id="Select-column-label" shrink={false}>
                    {t('common:column_name')}
                  </InputLabel>
                  <Select
                    labelId="Select-column-label"
                    id="Select-column"
                    name="column"
                    value={column}
                    onChange={handleInputChange}
                    input={<OutlinedInput label={t('common:column_name')} />}
                    IconComponent={KeyboardArrowDownIcon}
                    MenuProps={{ size: 'small', autoFocus: false }}
                  >
                    {columnsData.columns.map(column => (
                      <MenuItem key={column} value={column}>
                        <ListItemText primary={column} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </Box>

        <Button
          variant="contained"
          sx={{ display: 'flex', mx: 'auto', mt: 5, mb: 3 }}
          type="button"
          disabled={isClassifyBtnDisabled}
          onClick={handleSubmit}
        >
          {t('promptbar:classify')}
        </Button>
      </Box>

      {/* результат классификации */}
      {status !== STATUS_KEYS.IDLE && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2.5rem',
            mt: 2,
          }}
        >
          {data.source === SOURCE.TEXT && (
            <>
              {status === STATUS_KEYS.RESOLVED && (
                <>
                  <FormControl fullWidth variant="standard" sx={{ mt: 2 }}>
                    <InputLabel id="Input-cleaned-label" shrink={false}>
                      {t('classification:сleanedText')}
                    </InputLabel>
                    <Paper className="textfield" elevation={0}>
                      {cleanedText}
                    </Paper>
                  </FormControl>

                  {nondict && (
                    <FormControl fullWidth variant="standard">
                      <InputLabel id="Input-nondict-label" shrink={false}>
                        {t('Non-dictionary words')}
                      </InputLabel>
                      <Paper className="textfield" elevation={0}>
                        {nondict}
                      </Paper>
                    </FormControl>
                  )}

                  <Box sx={{ mt: 1 }}>
                    <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                      <InputLabel
                        id="Input-classification-label"
                        shrink={false}
                      >
                        {t('classification:classification')}
                      </InputLabel>
                    </FormControl>
                    <FunctionalTable
                      data={classification}
                      columns={columns}
                      isActionColumn={false}
                      isFilter={false}
                    />
                  </Box>
                </>
              )}
              {status === STATUS_KEYS.PENDING && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} thickness={3} color="secondary" />
                  <Typography component="p" sx={{ fontWeight: 'bold' }}>
                    {t('The classification is in progress') + '...'}
                  </Typography>
                </Box>
              )}
              {status === STATUS_KEYS.REJECTED && (
                <Typography component="p" sx={{ fontWeight: 'bold' }} mb={3}>
                  {t('An error occurred during the classification process') +
                    '.'}
                </Typography>
              )}
            </>
          )}

          {data.source === SOURCE.FILE && (
            <FileProcessingCard
              pendingText={
                t('The classification of the file is in progress', {
                  fileName: fileName ? ` "${fileName}"` : '',
                }) + '...'
              }
              resolvedText={
                t('The classification of the file is complete', {
                  fileName: fileName ? ` "${fileName}"` : '',
                }) + '!'
              }
              errorText={
                t('An error occurred during the classification of the file', {
                  fileName: fileName ? ` "${fileName}"` : '',
                }) + '.'
              }
              fileId={fileResultTaskId}
              status={status}
              fileResultUrl={fileResultUrl}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default ClassificationView;
